/**
 * Game Registry - Metadata and lazy component loaders for each game type
 *
 * Each game type registers its display name, player count constraints,
 * and lazy-loaded screen components.
 */

import { type ComponentType, type LazyExoticComponent, lazy } from "react";
import { StoneFlickingCreateGameForm } from "./games/augmented-stone-flicking/StoneFlickingCreateGameForm";
import { BlackAndWhiteCreateGameForm } from "./games/black-and-white/BlackAndWhiteCreateGameForm";
import { ChessCreateGameForm } from "./games/chess/ChessCreateGameForm";
import { GreatKingdomCreateGameForm } from "./games/great-kingdom/GreatKingdomCreateGameForm";
import { IndianPokerCreateGameForm } from "./games/indian-poker/IndianPokerCreateGameForm";
import { MinusAuctionCreateGameForm } from "./games/minus-auction/MinusAuctionCreateGameForm";
import { TriAngerCreateGameForm } from "./games/tri-anger/TriAngerCreateGameForm";
import {
  YachtCreateGameForm,
  YachtRaceCreateGameForm,
} from "./games/yacht/CreateGameForm";

export interface CreateGameFormProps {
  readonly onConfigChange: (config: Record<string, unknown>) => void;
}

export interface GameTypeConfig {
  readonly gameType: string;
  readonly displayName: string;
  readonly description: string;
  readonly minPlayers: number;
  readonly maxPlayers: number;
  readonly icon: string;
  readonly iconBg: string;
  readonly supportsObservers?: boolean;
  /**
   * Waiting room offers an "AI 상대 초대" control. The bot roster itself comes
   * from runtime config (config.aiBots[gameType]); when a roster is present the
   * room shows a per-bot selector, otherwise it falls back to the generic BW
   * difficulty select (needs aiBotServiceUrl).
   */
  readonly supportsAiOpponent?: boolean;
  readonly GameScreen?: LazyExoticComponent<ComponentType<unknown>>;
  readonly CreateGameForm?: ComponentType<CreateGameFormProps>;
}

export interface RegisteredGameTypeConfig extends GameTypeConfig {
  readonly addedOrder: number;
}

const registry = new Map<string, RegisteredGameTypeConfig>();

export function normalizeGameType(gameType: string): string {
  return gameType;
}

export function registerGameType(config: GameTypeConfig): void {
  const normalizedGameType = normalizeGameType(config.gameType);
  const existing = registry.get(normalizedGameType);

  registry.set(normalizedGameType, {
    ...config,
    addedOrder: existing?.addedOrder ?? registry.size + 1,
  });
}

export function getGameType(
  gameType: string,
): RegisteredGameTypeConfig | undefined {
  return registry.get(normalizeGameType(gameType));
}

export function getAllGameTypes(): readonly RegisteredGameTypeConfig[] {
  return [...registry.values()].sort((a, b) => a.addedOrder - b.addedOrder);
}

export function isValidGameType(gameType: string): boolean {
  return registry.has(normalizeGameType(gameType));
}

// Lazy-loaded game screens for code splitting
const ChessGameScreen = lazy(() =>
  import("./games/chess/GameScreen").then((m) => ({ default: m.GameScreen })),
);

const AlkagiGameScreen = lazy(() =>
  import("./games/alkagi/MultiplayerGame").then((m) => ({
    default: m.MultiplayerGame,
  })),
);

const HorrorRaceGameScreen = lazy(() =>
  import("./games/horror-race/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const FruitShopGameScreen = lazy(() =>
  import("./games/fruit-shop/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const IndianPokerGameScreen = lazy(() =>
  import("./games/indian-poker/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const KingsCrownGameScreen = lazy(() =>
  import("./games/kings-crown/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const YachtGameScreen = lazy(() =>
  import("./games/yacht/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const FishShopGameScreen = lazy(() =>
  import("./games/fish-shop/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const MinusAuctionGameScreen = lazy(() =>
  import("./games/minus-auction/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const BlackAndWhiteGameScreen = lazy(() =>
  import("./games/black-and-white/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const GreatKingdomGameScreen = lazy(() =>
  import("./games/great-kingdom/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const AugmentedStoneFlickingGameScreen = lazy(() =>
  import("./games/augmented-stone-flicking/MultiplayerGame").then((m) => ({
    default: m.MultiplayerGame,
  })),
);

const YinshGameScreen = lazy(() =>
  import("./games/yinsh/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const WarchestGameScreen = lazy(() =>
  import("./games/warchest/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const CascadiaGameScreen = lazy(() =>
  import("./games/cascadia/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const CelestiaGameScreen = lazy(() =>
  import("./games/celestia/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const OneNightUltimateWerewolfGameScreen = lazy(() =>
  import("./games/one-night-ultimate-werewolf/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const TriAngerGameScreen = lazy(() =>
  import("./games/tri-anger/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const AugmentedWarGameScreen = lazy(() =>
  import("./games/augmented-war/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

const CarnivalOfSinsGameScreen = lazy(() =>
  import("./games/carnival-of-sins/GameScreen").then((m) => ({
    default: m.GameScreen,
  })),
);

registerGameType({
  gameType: "chess",
  displayName: "체스",
  description: "증강 체스 — 2인 대전",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "♟",
  iconBg: "#eff6ff",
  GameScreen: ChessGameScreen,
  CreateGameForm: ChessCreateGameForm,
});

registerGameType({
  gameType: "alkagi",
  displayName: "알까기",
  description: "알까기 — 2인 대전",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "⚪",
  iconBg: "#fff3e0",
  supportsObservers: true,
  GameScreen: AlkagiGameScreen,
});

registerGameType({
  gameType: "horror-race",
  displayName: "호러 레이스",
  description: "호러 레이스 — 3~12인 멀티플레이",
  minPlayers: 3,
  maxPlayers: 12,
  icon: "👻",
  iconBg: "#fce4ec",
  supportsObservers: true,
  GameScreen: HorrorRaceGameScreen,
});

registerGameType({
  gameType: "fruit-shop",
  displayName: "과일가게",
  description: "과일가게 — 13인 비공개 희망가·최저가",
  minPlayers: 13,
  maxPlayers: 13,
  icon: "●",
  iconBg: "#1c1914",
  supportsObservers: true,
  GameScreen: FruitShopGameScreen,
});

registerGameType({
  gameType: "indian-poker",
  displayName: "인디언 포커",
  description: "인디언 포커 — 2인 블러핑 베팅",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "◆",
  iconBg: "#213529",
  supportsObservers: true,
  GameScreen: IndianPokerGameScreen,
  CreateGameForm: IndianPokerCreateGameForm,
});

registerGameType({
  gameType: "kings-crown",
  displayName: "킹스 크라운",
  description: "킹스 크라운 — 2인 기억·빙고 추상 전략",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "K",
  iconBg: "#eef2f6",
  supportsObservers: true,
  GameScreen: KingsCrownGameScreen,
});

registerGameType({
  gameType: "yacht",
  displayName: "Yacht · 요트",
  description: "Yacht — 2~6인 턴제 다이스 점수 게임",
  minPlayers: 2,
  maxPlayers: 6,
  icon: "⚂",
  iconBg: "#0f172a",
  supportsObservers: true,
  GameScreen: YachtGameScreen,
  CreateGameForm: YachtCreateGameForm,
});

registerGameType({
  gameType: "yacht-race",
  displayName: "Yacht Race · 요트 레이스",
  description: "Yacht Race — 4~12인 동시턴 다이스 점수 게임",
  minPlayers: 4,
  maxPlayers: 12,
  icon: "⚄",
  iconBg: "#172131",
  supportsObservers: true,
  GameScreen: YachtGameScreen,
  CreateGameForm: YachtRaceCreateGameForm,
});

registerGameType({
  gameType: "fish-shop",
  displayName: "생선가게",
  description: "생선가게 — 3~12인 비공개 판매권·희망가",
  minPlayers: 3,
  maxPlayers: 12,
  icon: "△",
  iconBg: "#1c1914",
  supportsObservers: true,
  GameScreen: FishShopGameScreen,
});

registerGameType({
  gameType: "minus-auction",
  displayName: "마이너스 경매",
  description: "마이너스 경매 — 3~10인 공개 순차 경매",
  minPlayers: 3,
  maxPlayers: 10,
  icon: "−",
  iconBg: "#2d2018",
  supportsObservers: true,
  GameScreen: MinusAuctionGameScreen,
  CreateGameForm: MinusAuctionCreateGameForm,
});

registerGameType({
  gameType: "black-and-white",
  displayName: "흑과백",
  description: "흑과백 — 2인 비공개 숫자 추리",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "◐",
  iconBg: "#f4f6f8",
  supportsObservers: true,
  supportsAiOpponent: true,
  GameScreen: BlackAndWhiteGameScreen,
  CreateGameForm: BlackAndWhiteCreateGameForm,
});

registerGameType({
  gameType: "great-kingdom",
  displayName: "그레이트 킹덤",
  description: "그레이트 킹덤 — 2인 영토 포위 전략",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "▦",
  iconBg: "#101820",
  supportsObservers: true,
  GameScreen: GreatKingdomGameScreen,
  CreateGameForm: GreatKingdomCreateGameForm,
});

registerGameType({
  gameType: "augmented-stone-flicking",
  displayName: "증강 알까기",
  description: "증강 알까기 — 2~4인 동시 턴",
  minPlayers: 2,
  maxPlayers: 4,
  icon: "🔴",
  iconBg: "#f3e5f5",
  GameScreen: AugmentedStoneFlickingGameScreen,
  CreateGameForm: StoneFlickingCreateGameForm,
});

registerGameType({
  gameType: "yinsh",
  displayName: "YINSH",
  description: "YINSH — 2인 대전",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "⬡",
  iconBg: "#e8f5e9",
  supportsObservers: true,
  // The selectable champion roster is config-driven (config.aiBots["yinsh"],
  // from the GAME_AI_BOTS env var) — no per-model wiring lives here.
  supportsAiOpponent: true,
  GameScreen: YinshGameScreen,
});

registerGameType({
  gameType: "warchest",
  displayName: "War Chest",
  description: "War Chest — 2인 대전",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "⚔",
  iconBg: "#fef3c7",
  supportsObservers: true,
  GameScreen: WarchestGameScreen,
});

registerGameType({
  gameType: "cascadia",
  displayName: "Cascadia",
  description: "Cascadia — 2~4인 타일·토큰 드래프트",
  minPlayers: 2,
  maxPlayers: 4,
  icon: "🌲",
  iconBg: "#e0f2fe",
  GameScreen: CascadiaGameScreen,
});

registerGameType({
  gameType: "celestia",
  displayName: "Celestia",
  description: "Celestia (셀레스티아) — 2~6인 히든 핸드 블러핑",
  minPlayers: 2,
  maxPlayers: 6,
  icon: "✈",
  iconBg: "#eef2ff",
  supportsObservers: true,
  GameScreen: CelestiaGameScreen,
});

registerGameType({
  gameType: "one-night-ultimate-werewolf",
  displayName: "원나잇 얼티밋 웨어울프",
  description: "원나잇 얼티밋 웨어울프 — 3~10인 사회 추론",
  minPlayers: 3,
  maxPlayers: 10,
  icon: "🌙",
  iconBg: "#e2f0ed",
  supportsObservers: true,
  GameScreen: OneNightUltimateWerewolfGameScreen,
});

registerGameType({
  gameType: "tri-anger",
  displayName: "Tri-Anger",
  description: "Tri-Anger — 2인 6x6 점령 전술",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "△",
  iconBg: "#eaf2ff",
  GameScreen: TriAngerGameScreen,
  CreateGameForm: TriAngerCreateGameForm,
});

// 말달리자 (run-horses) is registered for the replay/simulation surface only
// (enables isValidGameType so /run-horses/replay/<scenario> routes). Live
// multiplayer (GameScreen) is not wired yet — replay is backend-free.
registerGameType({
  gameType: "run-horses",
  displayName: "말달리자",
  description: "말달리자 — 11×11 오아시스 선착 (리플레이/시뮬레이션)",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "🐎",
  iconBg: "#e9d8b0",
});

// 증강 전쟁 (augmented-war) — 2P hex war with zoned ZoC near homes and secret
// two-step element combat (DeclareAttack → ResolveAttack). Live multiplayer +
// backend-free replay both wired.
registerGameType({
  gameType: "augmented-war",
  displayName: "증강 전쟁",
  description: "증강 전쟁 — 2인 헥스 전쟁, 원소 심리전 (홈 구역 ZoC)",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "⚔",
  iconBg: "#e6dcc8",
  supportsObservers: true,
  GameScreen: AugmentedWarGameScreen,
});

// Carnival of Sins (죄악의 카니발) — replay + live multiplayer. Almost no
// hidden information (only a not-yet-revealed pending selection is masked,
// via apps/board-game-rules-carnival-of-sins-v2's private-viewer-state
// projection). Staging/prod deploy of the rule service is not wired yet —
// see docs/task-log for the current gap.
registerGameType({
  gameType: "carnival-of-sins",
  displayName: "Carnival of Sins · 죄악의 카니발",
  description: "죄악의 카니발 — 2-5인 7라운드 세트-컬렉션 다이스",
  minPlayers: 2,
  maxPlayers: 5,
  icon: "🎭",
  iconBg: "#2a2118",
  supportsObservers: true,
  GameScreen: CarnivalOfSinsGameScreen,
});

// 기억의 만찬 (memory-feast) — replay/simulation surface only (hidden-info
// memory game). Replay is backend-free; live multiplayer not wired yet.
registerGameType({
  gameType: "memory-feast",
  displayName: "기억의 만찬",
  description: "기억의 만찬 — 같은 수 그릇쌍 찾기 (리플레이/시뮬레이션)",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "🍲",
  iconBg: "#e7d3b3",
});

// 망각의 지뢰 (oblivion-mine) — replay/simulation surface only (asymmetric
// hidden-info minefield race). Replay is backend-free; live multiplayer not
// wired yet.
registerGameType({
  gameType: "oblivion-mine",
  displayName: "망각의 지뢰",
  description: "망각의 지뢰 — 지뢰 매설·추리·보물 레이스 (리플레이/시뮬레이션)",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "💣",
  iconBg: "#b9a78c",
});

// 쇼 미 더 코인 (show-me-the-coin) — replay/simulation surface only (hidden-info
// commit-reveal + betting). Replay is backend-free; live multiplayer not wired.
registerGameType({
  gameType: "show-me-the-coin",
  displayName: "쇼 미 더 코인",
  description: "쇼 미 더 코인 — 코인 비공개 제출 + 베팅 (리플레이/시뮬레이션)",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "🪙",
  iconBg: "#f5c542",
});

// 러브 윈즈 올 (love-wins-all) — replay/simulation surface only (hidden-info
// RPS-hierarchy 족보 + betting). Replay is backend-free; live multiplayer not wired.
registerGameType({
  gameType: "love-wins-all",
  displayName: "러브 윈즈 올",
  description: "러브 윈즈 올 — 가위바위보 족보 + 베팅 (리플레이/시뮬레이션)",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "💕",
  iconBg: "#ec4899",
});

// 큐브지옥 (cube-hell) — replay/simulation surface only (hidden-info 3×3×3 cube
// deduction). Replay is backend-free; live multiplayer not wired.
registerGameType({
  gameType: "cube-hell",
  displayName: "큐브지옥",
  description: "큐브지옥 — 3×3×3 숨김 큐브 추리 (리플레이/시뮬레이션)",
  minPlayers: 2,
  maxPlayers: 2,
  icon: "🧊",
  iconBg: "#f97316",
});
